/**
 * markdown.tsx (T27.2.10.2) — a small, dependency-free Markdown→React renderer
 * for the public legal surface.
 *
 * Why in-house, not a library: the legal pages render the `docs/legal/*.md`
 * drafts (raw-imported, no forked copy — AC), and the drafts use a constrained,
 * well-formed Markdown subset: ATX headings (`#`–`######`), thematic breaks
 * (`---`), block quotes (`>`), GitHub tables (`| a | b |` + a `---` separator
 * row), unordered lists (`-`), paragraphs, and inline `**bold**`, `*italic*`,
 * `` `code` ``, and `[text](url)` links. A 6KB Markdown dependency would carry a
 * full CommonMark/GFM surface we don't need plus its own table quirks; this
 * renderer covers exactly the draft subset and is unit-tested against it.
 *
 * Safety: this NEVER renders raw HTML. Every text run is emitted as a React text
 * node (React escapes it), so a stray `<script>` in a draft would show as literal
 * text, not execute — the renderer-injects-HTML risk noted in the task is closed
 * by construction. Links are rendered only for `http(s):`, `mailto:`, in-app
 * absolute paths (`/…`), and relative `.md` siblings (rewritten to their public
 * route); any other target is dropped to plain text.
 *
 * Styling is token-only (CP5): block elements get app-token inline styles; no
 * hardcoded color or px.
 */

import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../../routes/paths';

// ---------------------------------------------------------------------------
// Token-only block styles
// ---------------------------------------------------------------------------

// T47 Phase 4 — rendered legal-doc headings (h1/h2/h3) take the Poppins
// display face; the body paragraphs/lists keep inheriting Inter (sans).
const h1Style: CSSProperties = {
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  lineHeight: 'var(--line-height-tight)',
  margin: 0,
  marginBottom: 'var(--space-4)',
  color: 'var(--color-text-primary)',
};

const h2Style: CSSProperties = {
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semibold)',
  lineHeight: 'var(--line-height-tight)',
  margin: 0,
  marginTop: 'var(--space-6)',
  marginBottom: 'var(--space-3)',
  color: 'var(--color-text-primary)',
};

const h3Style: CSSProperties = {
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-semibold)',
  margin: 0,
  marginTop: 'var(--space-4)',
  marginBottom: 'var(--space-2)',
  color: 'var(--color-text-primary)',
};

const pStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-3)',
  lineHeight: 'var(--line-height-relaxed)',
  color: 'var(--color-text-primary)',
};

const ulStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-3)',
  paddingLeft: 'var(--space-6)',
  lineHeight: 'var(--line-height-relaxed)',
  color: 'var(--color-text-primary)',
};

const liStyle: CSSProperties = {
  marginBottom: 'var(--space-1)',
};

const blockquoteStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-4)',
  borderLeftWidth: 'var(--border-width-hairline)',
  borderLeftStyle: 'solid',
  borderLeftColor: 'var(--color-border-strong)',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
};

const hrStyle: CSSProperties = {
  border: 'none',
  height: 'var(--border-width-hairline)',
  backgroundColor: 'var(--color-border-subtle)',
  margin: 'var(--space-5) 0',
};

const tableWrapStyle: CSSProperties = {
  width: '100%',
  overflowX: 'auto',
  marginBottom: 'var(--space-4)',
};

const tableStyle: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 'var(--font-size-sm)',
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  borderBottomWidth: 'var(--border-width-hairline)',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-strong)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  verticalAlign: 'top',
};

const tdStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  borderBottomWidth: 'var(--border-width-hairline)',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  color: 'var(--color-text-primary)',
  verticalAlign: 'top',
};

const codeStyle: CSSProperties = {
  fontFamily: 'var(--font-family-mono, monospace)',
  fontSize: '0.875em',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-sm)',
  padding: '0.0625rem var(--space-1)',
};

const linkStyle: CSSProperties = {
  color: 'var(--color-accent, var(--color-text-primary))',
  textDecoration: 'underline',
};

// ---------------------------------------------------------------------------
// Sibling-link rewrite — a `[Privacy Policy](privacy-policy.md)` cross-link in a
// draft must point at the deployed route, not the source file. Map every known
// draft filename to its public path; unknown `.md` targets render as plain text.
// ---------------------------------------------------------------------------

const SIBLING_ROUTE: Record<string, string> = {
  'privacy-policy.md': routes.privacy,
  'terms-of-service.md': routes.terms,
  'eula.md': routes.eula,
  'cookie-policy.md': routes.cookies,
};

/** Strip a leading `#anchor` and return just the filename portion of a relative target. */
function siblingFileOf(target: string): string {
  const noAnchor = target.split('#')[0] ?? target;
  const slash = noAnchor.lastIndexOf('/');
  return slash === -1 ? noAnchor : noAnchor.slice(slash + 1);
}

// ---------------------------------------------------------------------------
// Inline parsing — `**bold**`, `*italic*`, `` `code` ``, `[text](url)`.
//
// Order matters: code spans are extracted first (their content is literal), then
// links, then bold, then italic. Everything else is a plain text node (React
// escapes it). A `keyPrefix` keeps React keys stable + unique within a block.
// ---------------------------------------------------------------------------

const INLINE_RE =
  /(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)/;

function renderLink(token: string, key: string): ReactNode {
  // token: `[text](target)`
  const close = token.indexOf('](');
  const text = token.slice(1, close);
  const target = token.slice(close + 2, token.length - 1);

  if (/^(https?:|mailto:)/i.test(target)) {
    const external = /^https?:/i.test(target);
    return (
      <a
        key={key}
        href={target}
        style={linkStyle}
        {...(external ? { target: '_blank', rel: 'noreferrer noopener' } : {})}
      >
        {renderInline(text, `${key}t`)}
      </a>
    );
  }
  if (target.startsWith('/')) {
    return (
      <Link key={key} to={target} style={linkStyle}>
        {renderInline(text, `${key}t`)}
      </Link>
    );
  }
  // Relative sibling draft → its public route (cross-links between legal pages).
  const file = siblingFileOf(target);
  const mapped = SIBLING_ROUTE[file];
  if (mapped) {
    return (
      <Link key={key} to={mapped} style={linkStyle}>
        {renderInline(text, `${key}t`)}
      </Link>
    );
  }
  // Unknown target (e.g. an internal checklist .md not on the public surface):
  // render the link TEXT only, never an unresolved href.
  return <span key={key}>{renderInline(text, `${key}t`)}</span>;
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let i = 0;
  while (rest.length > 0) {
    const m = INLINE_RE.exec(rest);
    if (!m || m.index === undefined) {
      out.push(rest);
      break;
    }
    if (m.index > 0) out.push(rest.slice(0, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith('`')) {
      out.push(
        <code key={key} style={codeStyle}>
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('[')) {
      out.push(renderLink(token, key));
    } else if (token.startsWith('**')) {
      out.push(<strong key={key}>{renderInline(token.slice(2, -2), key)}</strong>);
    } else {
      out.push(<em key={key}>{renderInline(token.slice(1, -1), key)}</em>);
    }
    rest = rest.slice(m.index + token.length);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Block parsing
// ---------------------------------------------------------------------------

function isTableSeparator(line: string): boolean {
  // A GitHub table separator: `| --- | :---: |` — only pipes, dashes, colons,
  // spaces, with at least one dash.
  return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(line) && line.includes('-');
}

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/**
 * Render a Markdown string (the legal draft subset) to React nodes. Returns a
 * flat list of block elements; the caller wraps them in the page container.
 */
export function renderLegalMarkdown(source: string): ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;
  const nextKey = (): string => `b${key++}`;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    // Blank line — skip.
    if (trimmed === '') {
      i += 1;
      continue;
    }

    // Thematic break.
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push(<hr key={nextKey()} style={hrStyle} />);
      i += 1;
      continue;
    }

    // ATX heading.
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      const level = heading[1]?.length ?? 1;
      const content = heading[2] ?? '';
      const k = nextKey();
      if (level === 1) blocks.push(<h1 key={k} style={h1Style}>{renderInline(content, k)}</h1>);
      else if (level === 2) blocks.push(<h2 key={k} style={h2Style}>{renderInline(content, k)}</h2>);
      else blocks.push(<h3 key={k} style={h3Style}>{renderInline(content, k)}</h3>);
      i += 1;
      continue;
    }

    // Block quote — consume consecutive `>` lines.
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && (lines[i]?.trim().startsWith('>') ?? false)) {
        quoteLines.push((lines[i] ?? '').trim().replace(/^>\s?/, ''));
        i += 1;
      }
      const k = nextKey();
      // Join the quote's lines into one run (inner blank lines separate paragraphs;
      // the drafts keep quotes short, so render as text with <br/> on blanks).
      const parts: ReactNode[] = [];
      quoteLines.forEach((ql, idx) => {
        if (ql === '') {
          parts.push(<br key={`${k}-br${idx}`} />);
        } else {
          if (idx > 0 && quoteLines[idx - 1] !== '') parts.push(' ');
          parts.push(...renderInline(ql, `${k}-${idx}`));
        }
      });
      blocks.push(
        <blockquote key={k} style={blockquoteStyle}>
          {parts}
        </blockquote>,
      );
      continue;
    }

    // GitHub table — header row, separator row, then body rows.
    if (
      trimmed.includes('|') &&
      i + 1 < lines.length &&
      isTableSeparator(lines[i + 1] ?? '')
    ) {
      const headers = splitRow(line);
      i += 2; // skip header + separator
      const rows: string[][] = [];
      while (i < lines.length && (lines[i]?.includes('|') ?? false) && (lines[i]?.trim() ?? '') !== '') {
        rows.push(splitRow(lines[i] ?? ''));
        i += 1;
      }
      const k = nextKey();
      blocks.push(
        <div key={k} style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {headers.map((h, ci) => (
                  <th key={`${k}-h${ci}`} style={thStyle}>
                    {renderInline(h, `${k}-h${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={`${k}-r${ri}`}>
                  {headers.map((_, ci) => (
                    <td key={`${k}-r${ri}c${ci}`} style={tdStyle}>
                      {renderInline(r[ci] ?? '', `${k}-r${ri}c${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Unordered list — consume consecutive `-`/`*`/`+` items.
    if (/^[-*+]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i]?.trim() ?? '')) {
        items.push((lines[i] ?? '').trim().replace(/^[-*+]\s+/, ''));
        i += 1;
      }
      const k = nextKey();
      blocks.push(
        <ul key={k} style={ulStyle}>
          {items.map((it, idx) => (
            <li key={`${k}-${idx}`} style={liStyle}>
              {renderInline(it, `${k}-${idx}`)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph — gather consecutive non-blank lines that aren't another block.
    const paraLines: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      const t = l.trim();
      if (
        t === '' ||
        /^(#{1,6})\s+/.test(t) ||
        /^(-{3,}|\*{3,}|_{3,})$/.test(t) ||
        t.startsWith('>') ||
        /^[-*+]\s+/.test(t) ||
        (t.includes('|') && isTableSeparator(lines[i + 1] ?? ''))
      ) {
        break;
      }
      paraLines.push(t);
      i += 1;
    }
    const k = nextKey();
    const para: ReactNode[] = [];
    paraLines.forEach((pl, idx) => {
      if (idx > 0) para.push(' ');
      para.push(...renderInline(pl, `${k}-${idx}`));
    });
    blocks.push(
      <p key={k} style={pStyle}>
        {para}
      </p>,
    );
  }

  return blocks;
}
